import React, { useEffect, useState } from 'react';
import { dayShortAndClock } from '../lib/when';
import { supabase } from '../lib/supabase';
import { fetchAll, saveChanged } from '../lib/save';
import OwnershipTransferModal from './OwnershipTransferModal';
import Dialog from './Dialog';
import ListGrid from './ListGrid';
import { IconEye } from './dialogIcons';
import { isUncounted, submitState } from '../pages/countEntry/lineState';

/*
  A sheet somebody else holds, read only.

  Ported onto the shared dialog shell on 5 September 2026, to Group E's approved
  drawing `dialog-view-only-*.png`.

  **It printed a line nobody had counted as a 0**, which is the fifth place in
  OnHand that did and the one Group E named while drawing. A manager opening a
  colleague's sheet in progress saw a finished count of empty shelves rather
  than a count still being done. A blank says "not counted" in red now, and the
  strip at the top counts the blanks.

  **And it read the lines with one request**, which brings back about a thousand
  rows and says nothing about the rest. It pages now, like everything else that
  reads a list of unknown length.

  **The title is a sentence about a person**, from the drawing: "Michelle
  Chelette has this sheet". The old one was an amber banner reading "View Only -
  Count In Progress by Michelle Chelette", which is a status line about a record.
*/
export default function CountViewOnlyModal({ count, profile, onClose, toast, isAdmin = false, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [releasing, setReleasing] = useState(false);

  useEffect(() => {
    if (!count?.id) return;
    (async () => {
      const { data } = await fetchAll(() => supabase
        .from('count_line_items')
        .select('id, item_number_raw, description_raw, vendor_raw, quantity, notes, not_in_catalog, is_dead_stock, is_non_inventory, location, last_updated_at, last_updated_name')
        .eq('inventory_count_id', count.id)
        .order('item_number_raw'));
      setItems(data || []);
      setLoading(false);
    })();
  }, [count?.id]);

  const { total, blank, counted } = submitState(items);
  const totalUnits = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const lastUpdated = items.reduce((latest, i) => {
    if (!i.last_updated_at) return latest;
    return !latest || i.last_updated_at > latest ? i.last_updated_at : latest;
  }, null);

  const ownerName = count?.owner?.full_name || 'Another rep';
  const state = count?.status === 'not_started' ? 'Not started'
              : count?.status === 'in_progress' ? 'In progress'
              : count?.status === 'submitted'   ? 'Submitted'
              : 'Approved';

  const says = state + (lastUpdated ? ', last saved ' + dayShortAndClock(lastUpdated) : '')
    + '. You can read it. To count it yourself, ask an admin to transfer it to you.';

  /*
    **Checked, and it says what happened**, 18 September 2026 (the audit after
    the rebrand). It used to clear the holder without looking at the answer and
    close the window either way, so a refused take-back looked done.
  */
  async function release() {
    setReleasing(true);
    const ok = await saveChanged(toast, () => supabase.from('inventory_counts')
      .update({ rep_id: null }).eq('id', count.id).select('id'), 'taking the sheet back');
    setReleasing(false);
    if (!ok) return;
    toast?.success?.('Taken back. ' + (count?.warehouse?.name || 'The sheet') + ' is free for somebody else.');
    onChanged?.();
    onClose();
  }

  return (
    <>
      {showTransferModal && (
        <OwnershipTransferModal
          count={count} profile={profile} ownerName={ownerName}
          onClose={() => setShowTransferModal(false)} onSuccess={() => {}} toast={toast}
        />
      )}
      <Dialog
        kind="question" icon={<IconEye />} size="list"
        title={ownerName + ' has this sheet'}
        record={count?.warehouse?.name}
        says={says}
        onClose={onClose}
        footer={
          <>
            {/* An admin can take it back; a rep can ask for it. Rule 33: the
                way out sits on the left of the act. */}
            {isAdmin
              ? <button className="btn btn-quiet" onClick={release} disabled={releasing}>
                  {releasing ? 'Taking it back...' : 'Take it back off ' + ownerName.split(' ')[0]}
                </button>
              : <button className="btn btn-quiet" onClick={() => setShowTransferModal(true)}>Ask for this sheet</button>}
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </>
        }
      >
        {/* Rule 5, and rule 9: the blanks are counted and named. */}
        <div className="figure-strip" style={{ paddingBottom: 14, borderBottom: '1px solid var(--border-soft)', marginBottom: 14 }}>
          <Figure n={total} label="Lines" dot="var(--text-primary)" />
          <span className="tb-vr" style={{ margin: '0 14px' }} />
          <Figure n={counted} label="Counted" dot="var(--success)" />
          <span className="tb-vr" style={{ margin: '0 14px' }} />
          <Figure n={blank} label="Still blank" dot="var(--text-muted)" />
          <span className="tb-vr" style={{ margin: '0 14px' }} />
          <Figure n={totalUnits} label="Units so far" dot="var(--text-primary)" />
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>No lines on this sheet yet.</div>
        ) : (
          <ListGrid
            stickyHead
            columns={[
              { label: 'Item',        width: '1.2fr' },
              { label: 'Description', width: '2.6fr' },
              { label: 'Vendor',      width: '1.4fr' },
              { label: 'Qty',         width: '90px', align: 'right' },
            ]}
            rows={items}
            keyOf={i => i.id}
            rowClass={i => (isUncounted(i) ? 'state-blank' : 'state-counted')}
            cells={i => [
              <span style={{ fontWeight: 700, color: i.not_in_catalog ? 'var(--warning-ink)' : 'var(--action-text)' }}>{i.item_number_raw}</span>,
              <span>
                {i.description_raw}
                {i.notes && <span style={{ display: 'block', fontSize: 11, color: 'var(--info-ink)', fontStyle: 'italic', marginTop: 2 }}>Note: {i.notes}</span>}
                {i.last_updated_name && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>Counted by {i.last_updated_name}</span>}
              </span>,
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{i.vendor_raw || '--'}</span>,
              <Qty item={i} />,
            ]}
            /*
              **The narrow shape is a block, not six labelled lines.** The
              shared list's default puts a heading beside every value, and
              inside a dialog on a 390 phone that turned one line into a
              cramped two-column stack with the description wrapping four
              times. Found by opening the picture, which is the only thing that
              finds it.
            */
            stack={i => (
              <span>
                <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: i.not_in_catalog ? 'var(--warning-ink)' : 'var(--action-text)' }}>{i.item_number_raw}</span>
                  <Qty item={i} />
                </span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-primary)', marginTop: 2 }}>{i.description_raw}</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {[i.vendor_raw || '--', i.last_updated_name && ('counted by ' + i.last_updated_name)].filter(Boolean).join(' - ')}
                </span>
                {i.notes && <span style={{ display: 'block', fontSize: 11, color: 'var(--info-ink)', fontStyle: 'italic', marginTop: 2 }}>Note: {i.notes}</span>}
              </span>
            )}
          />
        )}
      </Dialog>
    </>
  );
}

// The same figure as the Submit dialog's, and for the same reason: rule 5 puts
// a live number on a hairline rather than in a tile.
function Figure({ n, label, dot }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, alignSelf: 'center', flexShrink: 0 }} />
      <b style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{typeof n === 'number' ? n.toLocaleString() : n}</b>
      <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{label}</span>
    </span>
  );
}

// The number, or the fact that nobody has been to that shelf. Rule 9.
function Qty({ item }) {
  if (isUncounted(item)) {
    return <span style={{ color: 'var(--text-secondary)', fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase' }}>Not counted</span>;
  }
  return <span style={{ fontWeight: 700 }}>{item.quantity}</span>;
}
